export interface RemotePlayer {
  id: string;
  x: number;
  y: number;
  angle: number;
  sailAngle: number;
  speed: number;
  color: number;
  shirtColor: number;
  hairStyle: number;
  hairColor: number;
  boatShape: string;
  ashore: boolean;
  personX: number;
  personY: number;
  name: string;
  boatAnimalIdx: number | null;
  petType: string | null;
  petType2: string | null;
  catColor: string | null;
  petOffX?: number;
  petOffY?: number;
  pet2OffX?: number;
  pet2OffY?: number;
  inside?: boolean;
  insideHouseId?: string | null;
  indoorX?: number;
  indoorY?: number;
}

export interface BuriedTreasure { id: string; x: number; y: number; by: string }
export interface DroppedItem { id: string; x: number; y: number; subtype: string; label: string; emoji: string }
export interface FridgeFood { id: string; subtype: string; label: string; emoji: string }
export interface FurnitureItem { id: string; type: string; color?: string; x: number; y: number; subroom?: string; food?: FridgeFood[] }
export interface House { id: string; x: number; y: number; by: string; type?: string; furniture?: FurnitureItem[] }
export interface Park { id: string; x: number; y: number; by: string }
export interface Gate { id: string; x1: number; y1: number; x2: number; y2: number; by: string }
export interface Bridge { id: string; x1: number; y1: number; x2: number; y2: number; by: string }
export interface BuiltIsland { id: string; x: number; y: number; by: string }
export type Season = 'vår' | 'sommar' | 'höst' | 'vinter';

export interface Network {
  readonly id: string | null;
  /** True once the server has restarted (redeployed) since we first connected. */
  readonly updateAvailable: boolean;
  readonly players: Map<string, RemotePlayer>;
  readonly windAngle: number | null;
  readonly season: Season | null;
  readonly collectedTreasures: Set<number>;
  readonly buriedTreasures: BuriedTreasure[];
  readonly droppedItems: DroppedItem[];
  readonly houses: House[];
  readonly parks: Park[];
  readonly gates: Gate[];
  readonly bridges: Bridge[];
  readonly builtIslands: BuiltIsland[];
  readonly killedAnimals: Set<number>;
  readonly movedAnimals: Map<number, { x: number; y: number }>;
  /**
   * Bumped whenever the world collections change (snapshot or delta). Lets the
   * renderer skip rebuilding static world geometry on frames where nothing
   * changed — which is almost all of them.
   */
  readonly worldVersion: number;
  readonly bandwidth: { sentPerSec: number; recvPerSec: number };
  sendAppearance(color: number, shirtColor: number, hairStyle: number, name: string, petType: string | null, petType2: string | null, catColor: string | null, hairColor: number, boatShape: string): void;
  sendPosition(x: number, y: number, angle: number, sailAngle: number, speed: number, ashore: boolean, personX: number, personY: number, boatAnimalIdx: number | null, petOffX?: number, petOffY?: number, pet2OffX?: number, pet2OffY?: number, inside?: boolean, insideHouseId?: string | null, indoorX?: number, indoorY?: number): void;
  collectTreasure(index: number): void;
  buryTreasure(x: number, y: number): void;
  digTreasure(id: string): void;
  dropItem(x: number, y: number, item: { subtype: string; label: string; emoji: string }): void;
  pickupItem(id: string): void;
  buildHouse(x: number, y: number, type?: string): void;
  demolishHouse(id: string): void;
  buildPark(x: number, y: number): void;
  demolishPark(id: string): void;
  buildGate(x1: number, y1: number, x2: number, y2: number): void;
  destroyGate(id: string): void;
  buildBridge(x1: number, y1: number, x2: number, y2: number): void;
  destroyBridge(id: string): void;
  buildIsland(x: number, y: number): void;
  demolishIsland(id: string): void;
  placeFurniture(houseId: string, item: { type: string; color?: string; x: number; y: number; subroom?: string }): void;
  removeFurniture(houseId: string, furnitureId: string): void;
  storeFood(houseId: string, furnitureId: string, item: { subtype: string; label: string; emoji: string }): void;
  takeFood(houseId: string, furnitureId: string, foodId: string): void;
  killAnimal(index: number): void;
  moveAnimal(index: number, x: number, y: number): void;
  destroy(): void;
}

/** Wire rounding — mirrors the server's. Keeps coords off 17-digit floats. */
const r1 = (n: number) => Math.round(n * 10) / 10;
const r3 = (n: number) => Math.round(n * 1000) / 1000;
const r2 = (n: number) => Math.round(n * 100) / 100;

interface Identified { id: string }

/**
 * An id-keyed world collection. The map is the source of truth for delta
 * application; the array is what callers iterate, rebuilt lazily so that a
 * per-frame `for (const h of net.houses)` stays allocation-free between edits.
 */
class WorldSet<T extends Identified> {
  private map = new Map<string, T>();
  private cache: T[] = [];
  private dirty = false;

  replaceAll(items: T[]) {
    this.map = new Map(items.map(i => [i.id, i]));
    this.dirty = true;
  }

  add(items: T[]) {
    for (const i of items) this.map.set(i.id, i);
    this.dirty = true;
  }

  remove(ids: string[]) {
    for (const id of ids) this.map.delete(id);
    this.dirty = true;
  }

  get list(): T[] {
    if (this.dirty) {
      this.cache = [...this.map.values()];
      this.dirty = false;
    }
    return this.cache;
  }
}

export function connectToServer(): Network {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  // Derive WS path relative to the page location (works behind any reverse proxy)
  const base = location.pathname.replace(/\/[^/]*$/, '');
  const url = `${proto}://${location.host}${base}/ws`;

  const state = {
    id: null as string | null,
    serverVersion: null as string | null,
    updateAvailable: false,
    players: new Map<string, RemotePlayer>(),
    windAngle: null as number | null,
    season: null as Season | null,
    collectedTreasures: new Set<number>(),
    buried: new WorldSet<BuriedTreasure>(),
    dropped: new WorldSet<DroppedItem>(),
    houses: new WorldSet<House>(),
    parks: new WorldSet<Park>(),
    gates: new WorldSet<Gate>(),
    bridges: new WorldSet<Bridge>(),
    builtIslands: new WorldSet<BuiltIsland>(),
    killedAnimals: new Set<number>(),
    movedAnimals: new Map<number, { x: number; y: number }>(),
    worldVersion: 0,
    pendingAppearance: null as object | null,
    ws: null as WebSocket | null,
    destroyed: false,
    lastSendTime: 0,
    // World revision we have applied. Compared against every tick's `rev` to
    // detect a missed delta.
    rev: -1,
    lastResyncRequest: 0,
    // bandwidth tracking
    bytesSentAccum: 0,
    bytesRecvAccum: 0,
    bytesSentRate: 0,
    bytesRecvRate: 0,
    bandwidthWindowStart: Date.now(),
  };

  function flushBandwidth() {
    const now = Date.now();
    const elapsed = (now - state.bandwidthWindowStart) / 1000;
    if (elapsed >= 1) {
      state.bytesSentRate = state.bytesSentAccum / elapsed;
      state.bytesRecvRate = state.bytesRecvAccum / elapsed;
      state.bytesSentAccum = 0;
      state.bytesRecvAccum = 0;
      state.bandwidthWindowStart = now;
    }
  }

  function send(data: string) {
    if (state.ws && state.ws.readyState === WebSocket.OPEN) {
      state.ws.send(data);
      state.bytesSentAccum += data.length;
      flushBandwidth();
    }
  }

  /** Ask for the full world again after a detected gap. Rate-limited so a
   *  persistent mismatch can't turn into a resync storm. */
  function requestResync() {
    const now = Date.now();
    if (now - state.lastResyncRequest < 2000) return;
    state.lastResyncRequest = now;
    console.warn('[net] world revision gap — requesting resync');
    send(JSON.stringify({ resync: 1 }));
  }

  /** Full world replace, from `welcome` or `snapshot`. */
  function applySnapshot(msg: any) {
    if (msg.windAngle != null) state.windAngle = msg.windAngle;
    if (msg.season != null) state.season = msg.season;
    state.collectedTreasures.clear();
    for (const i of msg.collectedTreasures ?? []) state.collectedTreasures.add(i);
    state.killedAnimals.clear();
    for (const i of msg.killedAnimals ?? []) state.killedAnimals.add(i);
    state.movedAnimals.clear();
    for (const m of msg.movedAnimals ?? []) state.movedAnimals.set(m.index, { x: m.x, y: m.y });
    state.buried.replaceAll(msg.buriedTreasures ?? []);
    state.dropped.replaceAll(msg.dropped ?? []);
    state.houses.replaceAll(msg.houses ?? []);
    state.parks.replaceAll(msg.parks ?? []);
    state.gates.replaceAll(msg.gates ?? []);
    state.bridges.replaceAll(msg.bridges ?? []);
    state.builtIslands.replaceAll(msg.builtIslands ?? []);
    state.rev = msg.rev ?? 0;
    state.worldVersion++;
  }

  /** Incremental world change. */
  function applyWorldDelta(msg: any) {
    if (msg.season)       state.season = msg.season;
    if (msg.addBuried)   state.buried.add(msg.addBuried);
    if (msg.delBuried)   state.buried.remove(msg.delBuried);
    if (msg.addDropped)  state.dropped.add(msg.addDropped);
    if (msg.delDropped)  state.dropped.remove(msg.delDropped);
    if (msg.addHouses)   state.houses.add(msg.addHouses);
    if (msg.delHouses)   state.houses.remove(msg.delHouses);
    if (msg.addParks)    state.parks.add(msg.addParks);
    if (msg.delParks)    state.parks.remove(msg.delParks);
    if (msg.addGates)    state.gates.add(msg.addGates);
    if (msg.delGates)    state.gates.remove(msg.delGates);
    if (msg.addBridges)  state.bridges.add(msg.addBridges);
    if (msg.delBridges)  state.bridges.remove(msg.delBridges);
    if (msg.addIslands)  state.builtIslands.add(msg.addIslands);
    if (msg.delIslands)  state.builtIslands.remove(msg.delIslands);
    if (msg.addCollected) for (const i of msg.addCollected) state.collectedTreasures.add(i);
    if (msg.delCollected) for (const i of msg.delCollected) state.collectedTreasures.delete(i);
    if (msg.addKilled)   for (const i of msg.addKilled) state.killedAnimals.add(i);
    if (msg.movedAnimals) for (const m of msg.movedAnimals) state.movedAnimals.set(m.index, { x: m.x, y: m.y });
    state.rev = msg.rev;
    state.worldVersion++;
  }

  /** Restore fields the server omitted when they held their default value. */
  function decodePlayer(p: any): RemotePlayer {
    return {
      id: p.id,
      x: p.x, y: p.y,
      angle: p.angle, sailAngle: p.sailAngle, speed: p.speed,
      color: p.color, shirtColor: p.shirtColor, hairStyle: p.hairStyle,
      hairColor: p.hairColor ?? 0x4a3520, boatShape: p.boatShape ?? 'classic',
      ashore: !!p.ashore,
      personX: p.personX ?? p.x,
      personY: p.personY ?? p.y,
      name: p.name ?? '',
      boatAnimalIdx: p.boatAnimalIdx ?? null,
      petType: p.petType ?? null,
      petType2: p.petType2 ?? null,
      catColor: p.catColor ?? null,
      petOffX: p.petOffX ?? 10,
      petOffY: p.petOffY ?? 8,
      pet2OffX: p.pet2OffX ?? -10,
      pet2OffY: p.pet2OffY ?? 8,
      inside: !!p.inside,
      insideHouseId: p.insideHouseId ?? null,
      indoorX: p.indoorX,
      indoorY: p.indoorY,
    };
  }

  function connect() {
    if (state.destroyed) return;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      state.ws = ws;
      // Re-send appearance on reconnect
      if (state.pendingAppearance) {
        send(JSON.stringify(state.pendingAppearance));
      }
    };

    ws.onmessage = (e) => {
      state.bytesRecvAccum += (e.data as string).length;
      flushBandwidth();
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'welcome') {
          state.id = msg.id;
          // A version that changed since our first welcome means the server
          // process restarted (i.e. was redeployed) while we were connected
          // — prompt the player to reload rather than keep running stale code.
          if (typeof msg.version === 'string') {
            if (state.serverVersion !== null && state.serverVersion !== msg.version) {
              state.updateAvailable = true;
            }
            state.serverVersion = msg.version;
          }
          applySnapshot(msg);
          console.log('[net] welcome windAngle:', msg.windAngle, 'rev:', state.rev);
        } else if (msg.type === 'snapshot') {
          applySnapshot(msg);
        } else if (msg.type === 'world') {
          // Deltas must arrive in order. A gap means we missed one.
          if (msg.rev === state.rev + 1) {
            applyWorldDelta(msg);
          } else if (msg.rev > state.rev + 1) {
            requestResync();
          }
          // msg.rev <= state.rev is a duplicate/replay — ignore.
        } else if (msg.type === 'state') {
          if (msg.windAngle != null) state.windAngle = msg.windAngle;
          if (msg.rev != null && msg.rev !== state.rev) requestResync();

          const seen = new Set<string>();
          for (const raw of msg.players) {
            if (raw.id === state.id) continue; // self is in the shared buffer
            seen.add(raw.id);
            state.players.set(raw.id, decodePlayer(raw));
          }
          // Remove players no longer in the broadcast
          for (const id of state.players.keys()) {
            if (!seen.has(id)) state.players.delete(id);
          }
        }
      } catch (err) { console.warn('[net] failed to parse message:', err); }
    };

    ws.onclose = () => {
      state.ws = null;
      state.rev = -1; // force a fresh snapshot on reconnect
      if (!state.destroyed) setTimeout(connect, 2000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }

  connect();

  return {
    get id() { return state.id; },
    get updateAvailable() { return state.updateAvailable; },
    get players() { return state.players; },
    get windAngle() { return state.windAngle; },
    get season() { return state.season; },
    get collectedTreasures() { return state.collectedTreasures; },
    get buriedTreasures() { return state.buried.list; },
    get droppedItems() { return state.dropped.list; },
    get houses() { return state.houses.list; },
    get parks() { return state.parks.list; },
    get gates() { return state.gates.list; },
    get bridges() { return state.bridges.list; },
    get builtIslands() { return state.builtIslands.list; },
    get killedAnimals() { return state.killedAnimals; },
    get movedAnimals() { return state.movedAnimals; },
    get worldVersion() { return state.worldVersion; },
    get bandwidth() { return { sentPerSec: state.bytesSentRate, recvPerSec: state.bytesRecvRate }; },

    buryTreasure(x: number, y: number) {
      send(JSON.stringify({ bury: { x: r1(x), y: r1(y) } }));
    },

    digTreasure(id: string) {
      send(JSON.stringify({ dig: id }));
    },

    dropItem(x: number, y: number, item: { subtype: string; label: string; emoji: string }) {
      send(JSON.stringify({ dropItem: { x: r1(x), y: r1(y), ...item } }));
    },

    pickupItem(id: string) {
      send(JSON.stringify({ pickupItem: id }));
    },

    collectTreasure(index: number) {
      state.collectedTreasures.add(index);
      send(JSON.stringify({ collect: index }));
    },

    sendAppearance(color, shirtColor, hairStyle, name, petType, petType2, catColor, hairColor, boatShape) {
      const msg = { type: 'appearance', color, shirtColor, hairStyle, name, petType, petType2, catColor, hairColor, boatShape };
      state.pendingAppearance = msg;
      send(JSON.stringify(msg));
    },

    sendPosition(x, y, angle, sailAngle, speed, ashore, personX, personY, boatAnimalIdx, petOffX, petOffY, pet2OffX, pet2OffY, inside, insideHouseId, indoorX, indoorY) {
      const now = Date.now();
      if (now - state.lastSendTime < 50) return; // throttle to 20Hz
      state.lastSendTime = now;
      const msg: Record<string, unknown> = {
        x: r1(x), y: r1(y),
        angle: r3(angle), sailAngle: r3(sailAngle), speed: r2(speed),
      };
      if (ashore) { msg.ashore = 1; msg.personX = r1(personX); msg.personY = r1(personY); }
      if (inside) {
        msg.inside = true;
        if (insideHouseId) { msg.insideHouseId = insideHouseId; msg.indoorX = r1(indoorX!); msg.indoorY = r1(indoorY!); }
      }
      msg.boatAnimalIdx = boatAnimalIdx ?? null;
      if (petOffX != null) { msg.petOffX = r1(petOffX); msg.petOffY = r1(petOffY!); }
      if (pet2OffX != null) { msg.pet2OffX = r1(pet2OffX); msg.pet2OffY = r1(pet2OffY!); }
      send(JSON.stringify(msg));
    },

    buildHouse(x: number, y: number, type: string = 'house') {
      send(JSON.stringify({ buildHouse: { x: r1(x), y: r1(y), type } }));
    },

    demolishHouse(id: string) {
      send(JSON.stringify({ demolishHouse: id }));
    },

    buildPark(x: number, y: number) {
      send(JSON.stringify({ buildPark: { x: r1(x), y: r1(y) } }));
    },

    demolishPark(id: string) {
      send(JSON.stringify({ demolishPark: id }));
    },

    buildGate(x1: number, y1: number, x2: number, y2: number) {
      send(JSON.stringify({ buildGate: { x1: r1(x1), y1: r1(y1), x2: r1(x2), y2: r1(y2) } }));
    },

    destroyGate(id: string) {
      send(JSON.stringify({ destroyGate: id }));
    },

    buildBridge(x1: number, y1: number, x2: number, y2: number) {
      send(JSON.stringify({ buildBridge: { x1: r1(x1), y1: r1(y1), x2: r1(x2), y2: r1(y2) } }));
    },

    destroyBridge(id: string) {
      send(JSON.stringify({ destroyBridge: id }));
    },

    buildIsland(x: number, y: number) {
      send(JSON.stringify({ buildIsland: { x: r1(x), y: r1(y) } }));
    },

    demolishIsland(id: string) {
      send(JSON.stringify({ demolishIsland: id }));
    },

    placeFurniture(houseId: string, item: { type: string; color?: string; x: number; y: number; subroom?: string }) {
      send(JSON.stringify({ placeFurniture: { houseId, item: { ...item, x: r1(item.x), y: r1(item.y) } } }));
    },

    removeFurniture(houseId: string, furnitureId: string) {
      send(JSON.stringify({ removeFurniture: { houseId, furnitureId } }));
    },

    storeFood(houseId: string, furnitureId: string, item: { subtype: string; label: string; emoji: string }) {
      send(JSON.stringify({ storeFood: { houseId, furnitureId, item } }));
    },

    takeFood(houseId: string, furnitureId: string, foodId: string) {
      send(JSON.stringify({ takeFood: { houseId, furnitureId, foodId } }));
    },

    killAnimal(index: number) {
      state.killedAnimals.add(index);
      send(JSON.stringify({ killAnimal: index }));
    },

    moveAnimal(index: number, x: number, y: number) {
      state.movedAnimals.set(index, { x: r1(x), y: r1(y) });
      send(JSON.stringify({ moveAnimal: { index, x: r1(x), y: r1(y) } }));
    },

    destroy() {
      state.destroyed = true;
      if (state.ws) state.ws.close();
    },
  };
}
